/**
 * customerCodeStorage.ts
 *
 * PWA「ホーム画面に追加」運用での customerCode 復元用 localStorage ヘルパー。
 *
 * 2026-05-16 致命バグ修正:
 *   旧仕様で法人 mode と個人 mode が同じ localStorage キーを共有していたため、
 *   個人 mode 利用が法人 LoginPage の pre-fill 値を汚染し
 *   「個人 mode から開いたら昨日の法人 customerCode が出る」事故になっていた。
 *   今版でキーを物理分離 (`weborder:corporate:*` / `weborder:individual:*`)、
 *   旧キーは migration で 1 度だけ法人キーへ移送して削除。
 *
 * PWA manifest の動的書換えは 5/16 一度試行したが iOS Safari が blob manifest を
 * 信用せず + 毎ページ /manifest.json fetch で重くなった失敗を経て撤退済 (f31adab)。
 * 本ファイルは storage 層のみ、network call 一切なし、初期表示への影響ゼロ。
 *
 * セキュリティ:
 *   - 保存対象は customerCode / tenantId / (個人 mode のみ) accessToken / shortCode のみ
 *   - パスワード / PIN は絶対に保存しない (開発憲法 8 条)
 *
 * 環境差分:
 *   - localStorage が使えない (プライベートブラウズ等) 環境では no-op + 空文字フォールバック
 *   - SSR (window 未定義) でも安全
 */

// ── 法人 mode 専用キー ──
const KEY_CORPORATE_CUSTOMER_CODE = 'weborder:corporate:lastCustomerCode';
const KEY_CORPORATE_TENANT_ID = 'weborder:corporate:lastTenantId';

// ── 個人 mode 専用キー ──
const KEY_INDIVIDUAL_TENANT_ID = 'weborder:individual:lastTenantId';
const KEY_INDIVIDUAL_CUSTOMER_CODE = 'weborder:individual:lastCustomerCode';
const KEY_INDIVIDUAL_ACCESS_TOKEN = 'weborder:individual:lastAccessToken';
const KEY_INDIVIDUAL_SHORT_CODE = 'weborder:individual:lastShortCode';

// ── 直近 mode 追跡 (LauncherPage の dispatch 優先度に使う) ──
// 2026-05-16: user fb「法人モード保存したつもりが個別モードに飛んだ」対応。
// 個別/法人キー両方ある mixed 端末で、最後に開いた mode を覚えてそっちへ dispatch する。
const KEY_LAST_MODE = 'weborder:lastMode';
export type LastMode = 'corporate' | 'individual';

// ── 旧 (互換) キー ──
const LEGACY_KEY_CUSTOMER_CODE = 'weborder:lastCustomerCode';
const LEGACY_KEY_TENANT_ID = 'weborder:lastTenantId';

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * 旧 単一キーを法人キーへ 1 度だけ migrate する。
 *
 * - 法人キーが既に書かれていれば旧キーだけ削除 (上書きしない、新値優先)
 * - 法人キーが空なら旧キーの値を移送、その後旧キー削除
 * - 何度呼んでも安全 (旧キーが消えれば no-op)
 */
function migrateLegacyKeysToCorporateOnce(storage: Storage): void {
  try {
    const legacyCode = storage.getItem(LEGACY_KEY_CUSTOMER_CODE);
    const legacyTenant = storage.getItem(LEGACY_KEY_TENANT_ID);
    if (legacyCode === null && legacyTenant === null) return;

    const existingCode = storage.getItem(KEY_CORPORATE_CUSTOMER_CODE);
    const existingTenant = storage.getItem(KEY_CORPORATE_TENANT_ID);

    if (legacyCode && !existingCode) {
      storage.setItem(KEY_CORPORATE_CUSTOMER_CODE, legacyCode);
    }
    if (legacyTenant && !existingTenant) {
      storage.setItem(KEY_CORPORATE_TENANT_ID, legacyTenant);
    }

    storage.removeItem(LEGACY_KEY_CUSTOMER_CODE);
    storage.removeItem(LEGACY_KEY_TENANT_ID);
  } catch {
    // ignore — migration 失敗時も致命でない
  }
}

// ============================================================================
// 法人 mode API
// ============================================================================

/**
 * 法人 mode: QR 経由 (`/c/:customerCode`) で開いた時点で呼ぶ。
 *
 * 🚨 重要: この関数は **個人 mode から絶対に呼んではいけない**。
 *   個人 mode が法人キーを汚染すると、法人 LoginPage で「昨日の個人 mode 顧客コード」が
 *   pre-fill される事故になる (2026-05-16 致命バグ)。
 */
export function saveLastCustomerCode(customerCode: string, tenantId?: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    migrateLegacyKeysToCorporateOnce(storage);
    const code = (customerCode || '').trim();
    if (code) storage.setItem(KEY_CORPORATE_CUSTOMER_CODE, code);
    if (tenantId !== undefined) {
      const t = (tenantId || '').trim();
      if (t) storage.setItem(KEY_CORPORATE_TENANT_ID, t);
    }
    // 法人 mode を最後に開いた → lastMode='corporate' (Launcher の dispatch 優先用)
    if (code) storage.setItem(KEY_LAST_MODE, 'corporate');
  } catch {
    // QuotaExceeded など、保存失敗は致命でないので握り潰す
  }
}

/**
 * 法人 mode の最後の customerCode を返す。LoginPage 復元用。
 * 個人 mode で書かれた値は **絶対に** 返さない (キーが完全分離されている)。
 */
export function getLastCustomerCode(): string {
  const storage = getStorage();
  if (!storage) return '';
  try {
    migrateLegacyKeysToCorporateOnce(storage);
    return storage.getItem(KEY_CORPORATE_CUSTOMER_CODE) || '';
  } catch {
    return '';
  }
}

/**
 * 法人 mode の最後の tenantId を返す。
 */
export function getLastTenantId(): string {
  const storage = getStorage();
  if (!storage) return '';
  try {
    migrateLegacyKeysToCorporateOnce(storage);
    return storage.getItem(KEY_CORPORATE_TENANT_ID) || '';
  } catch {
    return '';
  }
}

/**
 * 法人 mode の保存値を削除する (将来 UI 用、現状未使用)。
 * 個人 mode キーは触らない (個別に `clearIndividualLink` を呼ぶ)。
 * lastMode='corporate' だった場合はクリア (個別キーが残ってれば個別へ自動切替される)。
 */
export function clearLastCustomerCode(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(KEY_CORPORATE_CUSTOMER_CODE);
    storage.removeItem(KEY_CORPORATE_TENANT_ID);
    storage.removeItem(LEGACY_KEY_CUSTOMER_CODE);
    storage.removeItem(LEGACY_KEY_TENANT_ID);
    if (storage.getItem(KEY_LAST_MODE) === 'corporate') {
      storage.removeItem(KEY_LAST_MODE);
    }
  } catch {
    // ignore
  }
}

// ============================================================================
// lastMode API (LauncherPage dispatch 優先度に使用)
// ============================================================================

/**
 * 直近に開いた mode を返す。null は未設定 (初回起動 or clear 後)。
 */
export function getLastMode(): LastMode | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const v = storage.getItem(KEY_LAST_MODE);
    if (v === 'corporate' || v === 'individual') return v;
    return null;
  } catch {
    return null;
  }
}

/**
 * lastMode を明示的に削除 (UI からのリセット用、現状未使用)。
 */
export function clearLastMode(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(KEY_LAST_MODE);
  } catch {
    // ignore
  }
}

// ============================================================================
// 個人 mode API
// ============================================================================

export interface IndividualLink {
  tenantId: string;
  customerCode: string;
  accessToken?: string;
  shortCode?: string;
}

/**
 * 個人 mode: 短縮 URL / accessToken URL を開いた時点で呼ぶ。
 *
 * 法人キーは **絶対に触らない** (致命バグ防止)。
 * 別顧客の QR を読んだ場合は新値で上書きされる。
 *
 * 2026-08-22 先回り監査#7 (stale 掃除):
 *   旧実装は accessToken/shortCode を「渡された時だけ」set し、顧客が変わっても旧値を温存した。
 *   入口ページ(/c/)は accessToken 無しで mount 即保存するため、別顧客カードを開くと
 *   customerCode=新・accessToken=旧 の混合状態になり、launcher(accessToken 優先)が
 *   /i/{t}/{新code}/{旧token} を合成 → 前の人のログイン画面 or 死んだリンク。
 *   → 顧客/テナント切替時は旧 accessToken/shortCode を落とす(掃除のみ・保存順序/対象キーは不変)。
 *   同一顧客の再訪(ホームアイコン正常系)は prev==new で一切消さない=挙動完全不変。
 *   掃除後も resolve 成功 → /i/ 遷移 → IndividualLoginPage mount の既存保存で新 token が即入る。
 */
export function saveIndividualLink(link: IndividualLink): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    const t = (link.tenantId || '').trim();
    const c = (link.customerCode || '').trim();
    // ── 先回り監査#7: stale accessToken/shortCode 掃除(新値が t+c 揃った呼び出しのみ判定) ──
    if (t && c) {
      const prevT = storage.getItem(KEY_INDIVIDUAL_TENANT_ID) || '';
      const prevC = storage.getItem(KEY_INDIVIDUAL_CUSTOMER_CODE) || '';
      if ((prevT && t !== prevT) || (prevC && c !== prevC)) {
        // (1) 顧客/テナント切替: 旧 accessToken/shortCode は前の顧客の持ち物=混合URLの原因。両方落とす
        //     (新値が引数に在れば直後の set で入る)。初回(prev 空)は不発火=従来通り。
        storage.removeItem(KEY_INDIVIDUAL_ACCESS_TOKEN);
        storage.removeItem(KEY_INDIVIDUAL_SHORT_CODE);
      } else if (link.shortCode !== undefined) {
        // (2) 同一顧客で別カード(shortCode 不一致=別 member or PIN再発行後の新カード): 旧 accessToken は
        //     前のカードの持ち物=「前の人の名前が出て自分の PIN が通らない」の原因。token だけ落とす。
        //     同一カード再訪(shortCode 一致)と shortCode 未指定の呼び出し(/i/ 直URL=IndividualLoginPage
        //     mount・手入力入口)は不発火。掃除後は resolve 成功時の /i/ mount 保存で新 token が入る。
        const sNew = (link.shortCode || '').trim();
        const prevS = storage.getItem(KEY_INDIVIDUAL_SHORT_CODE) || '';
        if (sNew && prevS && sNew !== prevS) {
          storage.removeItem(KEY_INDIVIDUAL_ACCESS_TOKEN);
        }
      }
    }
    if (t) storage.setItem(KEY_INDIVIDUAL_TENANT_ID, t);
    if (c) storage.setItem(KEY_INDIVIDUAL_CUSTOMER_CODE, c);
    if (link.accessToken !== undefined) {
      const a = (link.accessToken || '').trim();
      if (a) storage.setItem(KEY_INDIVIDUAL_ACCESS_TOKEN, a);
    }
    if (link.shortCode !== undefined) {
      const s = (link.shortCode || '').trim();
      if (s) storage.setItem(KEY_INDIVIDUAL_SHORT_CODE, s);
    }
    // 個別 mode を最後に開いた → lastMode='individual' (tenantId + customerCode 揃った時のみ)
    if (t && c) storage.setItem(KEY_LAST_MODE, 'individual');
  } catch {
    // ignore
  }
}

/**
 * 個人 mode の最後のリンク情報を返す。tenantId と customerCode 両方揃ってない時は null。
 */
export function getIndividualLink(): IndividualLink | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const t = storage.getItem(KEY_INDIVIDUAL_TENANT_ID) || '';
    const c = storage.getItem(KEY_INDIVIDUAL_CUSTOMER_CODE) || '';
    if (!t || !c) return null;
    const a = storage.getItem(KEY_INDIVIDUAL_ACCESS_TOKEN) || '';
    const s = storage.getItem(KEY_INDIVIDUAL_SHORT_CODE) || '';
    const link: IndividualLink = { tenantId: t, customerCode: c };
    if (a) link.accessToken = a;
    if (s) link.shortCode = s;
    return link;
  } catch {
    return null;
  }
}

/**
 * 個人 mode の保存値を削除する。法人キーは触らない。
 * lastMode='individual' だった場合はクリア (法人キーが残ってれば法人へ自動切替される)。
 */
export function clearIndividualLink(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(KEY_INDIVIDUAL_TENANT_ID);
    storage.removeItem(KEY_INDIVIDUAL_CUSTOMER_CODE);
    storage.removeItem(KEY_INDIVIDUAL_ACCESS_TOKEN);
    storage.removeItem(KEY_INDIVIDUAL_SHORT_CODE);
    if (storage.getItem(KEY_LAST_MODE) === 'individual') {
      storage.removeItem(KEY_LAST_MODE);
    }
  } catch {
    // ignore
  }
}
